import React from "react";
import { Box, Container, Heading, Text, VStack, Grid, GridItem, Icon, List, ListItem, ListIcon, HStack, Badge } from "@chakra-ui/react";
import { FaCheckCircle, FaHome, FaDollarSign, FaHandsHelping, FaFileAlt, FaCalculator, FaUsers } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const GRAPHITE = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const FinanceServicesFirstHome: React.FC = () => {
  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/finance/services/first-home"
        fallbackTitle="First Home Buyer Finance - One Finance Australia"
        fallbackDescription="Expert guidance for first home buyers. Access government grants, low deposit options, and dedicated support throughout your journey."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={8} alignItems="center">
            <VStack align="start" spacing={4}>
              <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
                First Home Buyer Specialists
              </Badge>
              <Heading size="2xl" fontWeight="bold">
                Your First Home Journey Starts Here
              </Heading>
              <Text fontSize="lg" color="rgba(255,255,255,0.85)">
                We make buying your first home simpler, clearer, and better supported.
                Access government grants, low deposit pathways, and expert guidance throughout your journey.
              </Text>
            </VStack>
            <Box textAlign="center" bg="rgba(255,255,255,0.1)" p={8} borderRadius="xl">
              <Icon as={FaHome} boxSize={24} mb={4} />
              <Text fontSize="3xl" fontWeight="bold">$10,000+</Text>
              <Text>First Home Grants &amp; Concessions</Text>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Grid */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          First Home Buyer Benefits
        </Heading>
        <Grid templateColumns={{ base: "1fr", lg: "repeat(3, 1fr)" }} gap={6}>
          <GridItem colSpan={{ base: 1, lg: 2 }} rowSpan={2}>
            <Box bg={SAND_LIGHT} p={10} borderRadius="2xl" h="100%" border="2px solid" borderColor={SAND}>
              <Icon as={FaDollarSign} boxSize={12} color={SAND} mb={4} />
              <Heading size="lg" mb={4}>Government Grants & Concessions</Heading>
              <List spacing={3}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  <strong>First Home Owner Grant:</strong> Up to $10,000 for new homes
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  <strong>Stamp Duty Concessions:</strong> Save thousands on purchase costs
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  <strong>First Home Guarantee:</strong> Buy with as little as 5% deposit
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  <strong>Regional Home Guarantee:</strong> Additional support for regional buyers
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={SAND} />
                  <strong>Shared Equity Schemes:</strong> Government co-contribution programs
                </ListItem>
              </List>
            </Box>
          </GridItem>

          <GridItem>
            <Box bg="white" p={8} borderRadius="xl" h="100%" border="1px solid" borderColor="gray.200">
              <Icon as={FaHandsHelping} boxSize={10} color={GRAPHITE} mb={3} />
              <Heading size="md" mb={3}>Low Deposit Options</Heading>
              <Text color="gray.700">
                Purchase with as little as 5% deposit. We help you access lender mortgage insurance waivers and guarantor options.
              </Text>
            </Box>
          </GridItem>

          <GridItem>
            <Box bg="white" p={8} borderRadius="xl" h="100%" border="1px solid" borderColor="gray.200">
              <Icon as={FaUsers} boxSize={10} color={GRAPHITE} mb={3} />
              <Heading size="md" mb={3}>Expert Guidance</Heading>
              <Text color="gray.700">
                Dedicated first home buyer specialists guide you through every step, from pre-approval to settlement.
              </Text>
            </Box>
          </GridItem>
        </Grid>
      </Container>

      {/* Requirements Checklist */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={12}>
            <VStack align="start" spacing={6}>
              <Heading size="lg">Eligibility Requirements</Heading>
              <List spacing={4}>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color={SAND} mt={1} />
                    <Box>
                      <Text fontWeight="bold">First Time Buyer</Text>
                      <Text fontSize="sm" color="gray.600">You haven't owned property before</Text>
                    </Box>
                  </HStack>
                </ListItem>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color={SAND} mt={1} />
                    <Box>
                      <Text fontWeight="bold">Australian Citizen or PR</Text>
                      <Text fontSize="sm" color="gray.600">Valid residency status required</Text>
                    </Box>
                  </HStack>
                </ListItem>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color={SAND} mt={1} />
                    <Box>
                      <Text fontWeight="bold">Owner-Occupier</Text>
                      <Text fontSize="sm" color="gray.600">Property must be your primary residence</Text>
                    </Box>
                  </HStack>
                </ListItem>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaCheckCircle} color={SAND} mt={1} />
                    <Box>
                      <Text fontWeight="bold">Age Requirement</Text>
                      <Text fontSize="sm" color="gray.600">Must be 18 years or older</Text>
                    </Box>
                  </HStack>
                </ListItem>
              </List>
            </VStack>

            <VStack align="start" spacing={6}>
              <Heading size="lg">What You'll Need</Heading>
              <List spacing={4}>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaFileAlt} color={GRAPHITE} mt={1} />
                    <Box>
                      <Text fontWeight="bold">Proof of Income</Text>
                      <Text fontSize="sm" color="gray.600">Payslips, tax returns, employment letter</Text>
                    </Box>
                  </HStack>
                </ListItem>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaFileAlt} color={GRAPHITE} mt={1} />
                    <Box>
                      <Text fontWeight="bold">Savings Evidence</Text>
                      <Text fontSize="sm" color="gray.600">Bank statements showing genuine savings</Text>
                    </Box>
                  </HStack>
                </ListItem>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaFileAlt} color={GRAPHITE} mt={1} />
                    <Box>
                      <Text fontWeight="bold">ID Documents</Text>
                      <Text fontSize="sm" color="gray.600">Driver's licence, passport, utility bills</Text>
                    </Box>
                  </HStack>
                </ListItem>
                <ListItem>
                  <HStack align="start">
                    <Icon as={FaFileAlt} color={GRAPHITE} mt={1} />
                    <Box>
                      <Text fontWeight="bold">Property Details</Text>
                      <Text fontSize="sm" color="gray.600">Contract of sale or property listing</Text>
                    </Box>
                  </HStack>
                </ListItem>
              </List>
            </VStack>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box bg={NAVY} color="white" p={12} borderRadius="2xl" textAlign="center">
          <Icon as={FaCalculator} boxSize={16} mb={6} />
          <Heading size="xl" mb={4}>Ready to Calculate Your Borrowing Power?</Heading>
          <Text fontSize="lg" mb={8} opacity={0.9}>
            Use our free calculator to find out how much you can borrow
          </Text>
          <HStack justify="center" spacing={4}>
            <Box
              as="button"
              px={8}
              py={3}
              bg={SAND}
              color={NAVY}
              borderRadius="lg"
              fontWeight="bold"
              _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
            >
              Calculate Now
            </Box>
            <Box
              as="button"
              px={8}
              py={3}
              bg="transparent"
              border="2px solid"
              borderColor="white"
              borderRadius="lg"
              fontWeight="bold"
              _hover={{ bg: "whiteAlpha.200" }}
            >
              Speak to Specialist
            </Box>
          </HStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesFirstHome;
